<template>
  <div class="sp-events-sort">
    <EventsSortMenu
      ref="menu"
      :draft="draftValue"
      :options="options"
      :select-id="selectId"
      :trigger-label="selectedLabel"
      :disabled="!isBound"
      :title="title"
      :close-text="closeText"
      @select="handleSelect"
      @apply="handleApply"
    />
  </div>
</template>

<script setup>
import { useEventListener } from "@vueuse/core";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import EventsSortMenu from "./components/EventsSortMenu.ce.vue";
import { useDistanceSortPrompt } from "./composables/useDistanceSortPrompt";
import { useSortSelect } from "./composables/useSortSelect";
import { props as propsDefinitions } from "./props.js";

const props = defineProps(propsDefinitions);

const menu = useTemplateRef("menu");
const draftValue = ref("");

const { options, selectedValue, isBound, apply } = useSortSelect(() => props.selectId);
const { shouldPromptFor, promptForLocation, isLocationResolutionEvent } = useDistanceSortPrompt(
  () => props.nearFilterSelector,
);

useEventListener(document, "change", (event) => {
  if (isLocationResolutionEvent(event, selectedValue.value)) {
    menu.value?.close({ restoreFocus: false });
  }
});

const selectedLabel = computed(() => {
  const selectedOption = options.value.find((option) => option.value === selectedValue.value) || options.value[0];
  if (!selectedOption?.label) {
    return "";
  }

  return props.triggerPrefix ? `${props.triggerPrefix}: ${selectedOption.label}` : selectedOption.label;
});

watch(selectedValue, (value) => {
  draftValue.value = value;
});

onMounted(() => {
  draftValue.value = selectedValue.value;
  if (shouldPromptFor(selectedValue.value)) {
    requestAnimationFrame(promptForLocation);
  }
});

function handleSelect(value) {
  draftValue.value = value;
  applySelection();
}

function handleApply() {
  // Done button: re-apply idempotently and skip the location prompt so users
  // who confirmed their distance choice without setting a location don't get
  // the near filter forced open.
  apply(draftValue.value);
}

function applySelection() {
  apply(draftValue.value);
  if (shouldPromptFor(draftValue.value)) {
    // Close the sort dialog before opening the near filter — interactions inside
    // the near filter don't bubble to document, so outside-click detection won't
    // auto-close the sort dialog.
    menu.value?.close({ restoreFocus: false });
    promptForLocation();
  }
}
</script>

<style>
:host {
  display: inline-block;
  position: relative;
}
</style>
